import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './Home';
import CrewSIMs from './pages/CrewSIMs';
import VrsUsage from './pages/VrsUsage';
import Profile from './pages/Profile';
import Callback from './Callback';
import { RequireAuth } from './RequireAuth';
import SignedOut from './SignedOut';
import Activate from './pages/Activate';
import AppLayout from './components/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import RequireMfa from './components/RequireMfa';
import RequireRole from './components/RequireRole';
import OrganizationList from './pages/admin/OrganizationList';
import OrganizationDetail from './pages/admin/OrganizationDetail';
import UserList from './pages/admin/UserList';
import UserDetail from './pages/admin/UserDetail';
import AssetList from './pages/admin/AssetList';
import AssetDetail from './pages/admin/AssetDetail';
import OrgsAndAssets from './pages/admin/OrgsAndAssets';
import SimInventory from './pages/admin/SimInventory';
import SimDetail from './pages/admin/SimDetail';
import TerminalDetail from './pages/admin/TerminalDetail';
import AuditLog from './pages/admin/AuditLog';
import YachtOnboarding from './pages/admin/YachtOnboarding';
import YachtSetupWizard from './pages/admin/YachtSetupWizard';
import Dashboard from './pages/admin/Dashboard';
import SyncHistory from './pages/admin/SyncHistory';
import EsimGuide from './pages/admin/EsimGuide';
import TerminalDashboard from './pages/TerminalDashboard';
import StarlinkTerminals2 from './pages/StarlinkTerminals2';

function Protected() {
  return (
    <RequireAuth>
      <RequireMfa>
        <ErrorBoundary>
          <AppLayout>
            <Routes>
              <Route path="/" element={<Home />}>
                <Route index element={<CrewSIMs />} />
                <Route path="crew-sims" element={<CrewSIMs />} />
                <Route path="vrs-usage" element={<VrsUsage />} />
              </Route>
              <Route path="/profile" element={<Profile />} />
              <Route path="/starlink" element={<StarlinkTerminals2 />} />
              <Route path="/starlink/:deviceId" element={<TerminalDashboard />} />
              <Route
                path="/admin/dashboard"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin']}>
                    <Dashboard />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/sync-history"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin']}>
                    <SyncHistory />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <OrganizationList />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/organizations/:id"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <OrganizationDetail />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <UserList />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/users/:id"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <UserDetail />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/orgs-assets"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <OrgsAndAssets />
                  </RequireRole>
                }
              />
              {/* /admin/assets list kept as a deep-link target for older bookmarks; the
                Fleet view is the primary entry point in the sidebar. */}
              <Route
                path="/admin/assets"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <AssetList />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/assets/:id"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <AssetDetail />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/sims"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <SimInventory />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/sims/:provider/:identifier"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <SimDetail />
                  </RequireRole>
                }
              />
              {/* KAN-105: Starlink terminal detail. Same roles as the inventory. */}
              <Route
                path="/admin/terminals/:deviceId"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <TerminalDetail />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/onboarding"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin', 'customer_admin']}>
                    <YachtOnboarding />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/wizard"
                element={
                  <RequireRole
                    roles={['super_admin', 'reseller_admin', 'customer_admin', 'asset_manager']}
                  >
                    <YachtSetupWizard />
                  </RequireRole>
                }
              />
              {/* KAN-167: reference page for eSIM bundles, coverage and provisioning. */}
              <Route
                path="/admin/esim-guide"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin']}>
                    <EsimGuide />
                  </RequireRole>
                }
              />
              <Route
                path="/admin/audit-log"
                element={
                  <RequireRole roles={['super_admin', 'reseller_admin']}>
                    <AuditLog />
                  </RequireRole>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppLayout>
        </ErrorBoundary>
      </RequireMfa>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="/callback" element={<Callback />} />
        <Route path="/signed-out" element={<SignedOut />} />
        {/* KAN-180: invite-link activation, public (the user has no password yet) */}
        <Route path="/activate" element={<Activate />} />
        <Route path="/*" element={<Protected />} />
      </Routes>
    </Router>
  );
}
